import type { DocumentBlock } from '@/api';
import type { WordSuggestion } from '@/types/playbookWordAddin';
import type {
  WordApi,
  WordCollection,
  WordParagraph,
  WordRange,
  WordSyncContext,
} from './wordTypes';
import { logger } from '@/utils/logger';

export interface ApplySuggestionResult {
  changeTrackingMode: string;
  enabledChangeTracking: boolean;
}

const WORD_SEARCH_MAX_LENGTH = 180;

function getWordApi(): WordApi | null {
  return (window as unknown as { Word?: WordApi }).Word ?? null;
}

function requireWordApi(): WordApi {
  const wordApi = getWordApi();
  if (!wordApi) {
    throw new Error('Word document context is not available in this host.');
  }
  return wordApi;
}

export async function readCurrentWordDocument(): Promise<DocumentBlock[]> {
  const wordApi = requireWordApi();
  let blocks: DocumentBlock[] = [];

  await wordApi.run(async context => {
    const body = context.document.body;
    try {
      blocks = await readParagraphBlocks(context, body);
    } catch (error) {
      logger.warn('Falling back to Word body text extraction:', error);
      body.load('text');
      await context.sync();
      const documentText = body.text;
      blocks = documentText ? [{ text: documentText, page_number: 1 }] : [];
    }
  });

  return blocks;
}

export async function applySuggestionToWord(
  suggestion: WordSuggestion
): Promise<ApplySuggestionResult> {
  const proposedText = normalizeWordInsertionText(suggestion.proposed_text ?? '');
  if (!proposedText.trim()) {
    throw new Error('This suggestion does not include text that can be applied.');
  }

  const wordApi = requireWordApi();
  const textToReplace = suggestion.text_to_replace;
  let applyResult: ApplySuggestionResult = {
    changeTrackingMode: 'Unknown',
    enabledChangeTracking: false,
  };

  await wordApi.run(async context => {
    applyResult = await ensureTrackedChangesEnabled(context, wordApi);

    if (textToReplace) {
      const firstMatch = await findFirstMatchingRange(context, textToReplace);
      if (!firstMatch) {
        throw new Error('Could not find the affected text in the current Word document.');
      }

      replaceRangeWithParagraphText(firstMatch, proposedText);
      await context.sync();
      return;
    }

    const selection = context.document.getSelection();
    insertParagraphTextAfterRange(selection, proposedText);
    await context.sync();
  });

  return applyResult;
}

export async function focusSuggestionInWord(suggestion: WordSuggestion): Promise<void> {
  const wordApi = requireWordApi();

  await wordApi.run(async context => {
    const range = await findSuggestionReferenceRange(context, suggestion);
    if (!range) {
      throw new Error('Could not find the affected text in the current Word document.');
    }

    range.select();
    await context.sync();
  });
}

export async function addSuggestionCommentToWord(suggestion: WordSuggestion): Promise<void> {
  const commentText = buildSuggestionCommentText(suggestion);
  if (!commentText) {
    throw new Error('This suggestion does not include text that can be added as a comment.');
  }

  assertWordCommentInsertionSupported();
  const wordApi = requireWordApi();

  await wordApi.run(async context => {
    const range = await findSuggestionReferenceRange(context, suggestion);
    if (!range) {
      throw new Error('Could not find the affected text in the current Word document.');
    }

    if (!range.insertComment) {
      throw new Error('Word comment insertion is not available for this suggestion.');
    }

    range.insertComment(commentText);
    await context.sync();
  });
}

/**
 * The document's non-empty paragraphs, in order. A page number is consumed for
 * every paragraph so the two stay aligned, and blanks are dropped afterwards.
 */
async function readParagraphBlocks(
  context: WordSyncContext,
  body: WordRange
): Promise<DocumentBlock[]> {
  const paragraphs = body.paragraphs;
  paragraphs.load('items/text');
  await context.sync();

  const pageNumbers = await readParagraphPageNumbers(context);

  return paragraphs.items
    .map((paragraph: WordParagraph) => ({
      text: paragraph.text.trim(),
      page_number: pageNumbers.shift() ?? 1,
    }))
    .filter((block: DocumentBlock) => block.text.length > 0);
}

async function readParagraphPageNumbers(context: WordSyncContext): Promise<number[]> {
  try {
    const pages = context.document.activeWindow?.activePane?.pages;
    if (!pages?.load) {
      return [];
    }

    pages.load();
    await context.sync();

    const pageParagraphCollections: {
      pageNumber: number;
      paragraphs: WordCollection<WordParagraph>;
    }[] = [];
    for (const [pageIndex, page] of pages.items.entries()) {
      const paragraphs = page.getRange().paragraphs;
      paragraphs.load('items/text');
      pageParagraphCollections.push({ pageNumber: pageIndex + 1, paragraphs });
    }

    await context.sync();

    return pageParagraphCollections.flatMap(({ pageNumber, paragraphs }) =>
      paragraphs.items.map(() => pageNumber)
    );
  } catch (error) {
    logger.warn('Falling back to page 1 for Word paragraph page numbers:', error);
    return [];
  }
}

function normalizeWordInsertionText(text: string): string {
  return text
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
}

function splitParagraphText(text: string): string[] {
  return normalizeWordInsertionText(text).split('\n');
}

function replaceRangeWithParagraphText(range: WordRange, text: string): void {
  const [firstParagraph = '', ...remainingParagraphs] = splitParagraphText(text);
  range.insertText(firstParagraph, 'Replace');

  let insertionRange = range;
  for (const paragraphText of remainingParagraphs) {
    insertionRange = insertionRange.insertParagraph(paragraphText, 'After');
  }
}

function insertParagraphTextAfterRange(range: WordRange, text: string): void {
  let insertionRange = range;
  for (const paragraphText of splitParagraphText(text)) {
    insertionRange = insertionRange.insertParagraph(paragraphText, 'After');
  }
}

function buildSuggestionCommentText(suggestion: WordSuggestion): string {
  const proposedText = normalizeWordInsertionText(suggestion.proposed_text ?? '').trim();
  if (proposedText) {
    const parts = ['Suggested change:', proposedText];
    const explanation = suggestion.explanation.trim();
    if (explanation) {
      parts.push('', 'Reasoning:', explanation);
    }

    return parts.join('\n');
  }

  return suggestion.explanation.trim();
}

function assertWordCommentInsertionSupported(): void {
  const isSupported = window.Office?.context?.requirements?.isSetSupported?.('WordApi', '1.4');
  if (isSupported === false) {
    throw new Error('Adding Word comments requires WordApi 1.4 support.');
  }
}

async function findSuggestionReferenceRange(
  context: WordSyncContext,
  suggestion: WordSuggestion
): Promise<WordRange | null> {
  for (const referenceText of getSuggestionReferenceTexts(suggestion)) {
    const documentMatch = await findFirstMatchingRange(context, referenceText);
    if (documentMatch) {
      return documentMatch;
    }
  }

  return null;
}

function getSuggestionReferenceTexts(suggestion: WordSuggestion): string[] {
  return Array.from(
    new Set(
      [suggestion.text_to_replace, ...(suggestion.extraction_highlighted_text ?? [])]
        .map(text => text?.trim() ?? '')
        .filter(Boolean)
    )
  );
}

async function findFirstMatchingRange(
  context: WordSyncContext,
  textToReplace: string
): Promise<WordRange | null> {
  const candidates = buildSearchCandidates(textToReplace);

  for (const candidate of candidates) {
    const exactMatch = await searchFirstRange(context, candidate, false);
    if (exactMatch) {
      return exactMatch;
    }

    const whitespaceTolerantMatch = await searchFirstRange(context, candidate, true);
    if (whitespaceTolerantMatch) {
      return whitespaceTolerantMatch;
    }
  }

  return null;
}

async function searchFirstRange(
  context: WordSyncContext,
  searchText: string,
  ignoreSpace: boolean
): Promise<WordRange | null> {
  try {
    const results = context.document.body.search(searchText, {
      ignorePunct: false,
      ignoreSpace,
      matchCase: false,
      matchWholeWord: false,
    });
    results.load('items');
    await context.sync();
    return results.items.at(0) ?? null;
  } catch (error) {
    logger.warn('Word document search failed:', error);
    return null;
  }
}

function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function buildSearchCandidates(text: string): string[] {
  return Array.from(
    new Set(
      [text, normalizeWhitespace(text), text.replace(/\r?\n+/g, ' ').trim()].flatMap(candidate =>
        buildSafeSearchCandidates(candidate)
      )
    )
  ).filter(candidate => candidate.trim().length > 0);
}

function buildSafeSearchCandidates(text: string): string[] {
  const normalized = normalizeWhitespace(text);
  if (!normalized) {
    return [];
  }

  if (normalized.length <= WORD_SEARCH_MAX_LENGTH) {
    return [normalized];
  }

  const sentenceCandidates = normalized
    .split(/(?<=[.!?;:])\s+/)
    .map(sentence => sentence.trim())
    .filter(sentence => sentence.length > 0 && sentence.length <= WORD_SEARCH_MAX_LENGTH);

  return [
    ...sentenceCandidates,
    normalized.slice(0, WORD_SEARCH_MAX_LENGTH).trim(),
    sliceAroundMiddle(normalized),
    normalized.slice(-WORD_SEARCH_MAX_LENGTH).trim(),
  ].filter(candidate => candidate.length > 0 && candidate.length <= WORD_SEARCH_MAX_LENGTH);
}

function sliceAroundMiddle(text: string): string {
  const start = Math.max(Math.floor((text.length - WORD_SEARCH_MAX_LENGTH) / 2), 0);
  return text.slice(start, start + WORD_SEARCH_MAX_LENGTH).trim();
}

async function ensureTrackedChangesEnabled(
  context: WordSyncContext,
  wordApi: WordApi
): Promise<ApplySuggestionResult> {
  const isSupported = window.Office?.context?.requirements?.isSetSupported?.('WordApi', '1.4');
  if (isSupported === false) {
    throw new Error('Applying review suggestions requires Word Track Changes support.');
  }

  const document = context.document;
  document.load('changeTrackingMode');
  await context.sync();

  const currentMode = document.changeTrackingMode;
  const offMode = wordApi.ChangeTrackingMode?.off ?? 'Off';
  const trackMineOnlyMode = wordApi.ChangeTrackingMode?.trackMineOnly ?? 'TrackMineOnly';

  if (currentMode === offMode) {
    document.changeTrackingMode = trackMineOnlyMode;
    await context.sync();
    return {
      changeTrackingMode: trackMineOnlyMode,
      enabledChangeTracking: true,
    };
  }

  return {
    changeTrackingMode: currentMode,
    enabledChangeTracking: false,
  };
}
